import type { CodexSubscriptionAuthStatus } from "@t3tools/contracts";
import type {
  CloudProviderConnection,
  CloudProviderConnectionPresentation,
  CloudProviderExecutionTarget,
} from "./CloudProviderSettings";

export type CodexCloudExecutionTarget = CloudProviderExecutionTarget;
export type CodexSubscriptionConnection = CloudProviderConnection;
export type CodexSubscriptionConnectionPresentation = CloudProviderConnectionPresentation;

export type CodexSubscriptionAuthAction = "connect" | "disconnect" | "refresh";

export function resolveCodexCloudExecutionTarget(input: {
  readonly placement: "local" | "cloud" | undefined;
  readonly sameDevice: boolean;
}): CodexCloudExecutionTarget {
  if (input.placement === "cloud") return "cloud";
  return input.sameDevice ? "local" : "remote";
}

export function presentCodexSubscriptionAuthStatus(
  status: CodexSubscriptionAuthStatus,
): CodexSubscriptionConnection {
  switch (status.state) {
    case "connected":
      return { status: "connected" };
    case "disconnected":
      return {
        status: status.cliAvailable ? "disconnected" : "error",
        ...(status.cliAvailable
          ? {}
          : { message: "Codex CLI is not available in this environment." }),
      };
    case "different-authentication":
      return {
        status: "error",
        message:
          "Codex is using a different authentication method. Sign in with a ChatGPT subscription to use this provider connection.",
      };
    case "unknown":
      return {
        status: "error",
        message: status.cliAvailable
          ? "Codex subscription status is unavailable."
          : "Codex CLI is not available in this environment.",
      };
  }
}

export function presentCodexSubscriptionConnection(
  connection: CodexSubscriptionConnection,
): CodexSubscriptionConnectionPresentation {
  switch (connection.status) {
    case "loading":
      return {
        label: "Checking connection",
        description: "Checking whether your Codex subscription is available here.",
        tone: "muted",
      };
    case "connected":
      return {
        label: "Connected",
        description: "Your Codex subscription is available in this environment.",
        tone: "success",
      };
    case "error":
      return {
        label: "Connection error",
        description: connection.message ?? "We could not verify the Codex subscription connection.",
        tone: "error",
      };
    case "disconnected":
      return {
        label: "Not connected",
        description: "Connect a Codex subscription before starting tasks here.",
        tone: "muted",
      };
  }
}

export function canRunCodexCloudTask(input: {
  readonly target: CodexCloudExecutionTarget;
  readonly connection: CodexSubscriptionConnection;
}): boolean {
  return input.target !== "cloud" || input.connection.status === "connected";
}

export function codexCloudExecutionTargetLabel(target: CodexCloudExecutionTarget): string {
  if (target === "cloud") return "Cloud";
  if (target === "remote") return "Remote";
  return "Local";
}
